import React from "react";
import { useApp } from "@/context/AppContext";
import { resolveMedia } from "@/lib/api";
import { SmartImage } from "@/components/SmartImage";
import useSEO from "@/hooks/useSEO";
import { PageHero, SectionHeading } from "@/components/primitives";
import { Reveal } from "@/components/Reveal";
import { Highlight } from "@/components/Highlight";

export default function APropos() {
  const { t, m, lang } = useApp();
  useSEO(
    { fr: "À propos | Agrivoire Export SARL", en: "About | Agrivoire Export SARL" },
    { fr: "Diarabousset Alhyko C. Jean-Jacques, fondateur & CEO, représentant officiel à titre personnel de la Côte d'Ivoire au sein de l'IGEI.", en: "Diarabousset Alhyko C. Jean-Jacques, founder & CEO, personal official representative of Côte d'Ivoire within IGEI." }
  );

  return (
    <div data-testid="apropos-page">
      <PageHero kicker={t("apropos", "hero_kicker")} title={<Highlight text={t("apropos", "hero_title")} word={lang === "fr" ? "exigence" : "standard"} className="text-[#F5821F]" />} />

      <section className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28" data-testid="founder-section">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-3xl">
              <SmartImage
                src={resolveMedia(m("apropos_founder"))}
                alt={t("apropos", "founder_name")}
                testId="founder-photo"
                className="aspect-[4/5] w-full"
                imgClassName="h-full w-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="font-montserrat text-xl font-bold text-white">{t("apropos", "founder_name")}</p>
                <p className="font-poppins text-sm text-[#8CC63F]">{t("apropos", "founder_role")}</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7 lg:self-center lg:pl-6">
            <p className="text-justify font-poppins text-lg leading-relaxed text-neutral-700 md:text-xl" data-testid="founder-bio">
              {t("apropos", "founder_bio")}
            </p>
            {t("apropos", "founder_bio2") ? (
              <p className="mt-5 text-justify font-poppins text-base leading-relaxed text-neutral-600 md:text-lg">
                {t("apropos", "founder_bio2")}
              </p>
            ) : null}
            {m("apropos_igei_logo") ? (
              <div className="mt-8 flex items-center gap-4 border-t border-black/10 pt-6" data-testid="igei-badge">
                <img
                  src={resolveMedia(m("apropos_igei_logo"))}
                  alt="IGEI — Izba Gospodarcza Eksporterów i Importerów"
                  className="h-14 w-auto max-h-16 object-contain md:h-16"
                />
                <span className="font-poppins text-xs font-semibold uppercase tracking-[0.14em] text-neutral-500">
                  {lang === "fr" ? "Représentation officielle · IGEI" : "Official representation · IGEI"}
                </span>
              </div>
            ) : null}
          </Reveal>
        </div>
      </section>

      <section className="bg-gradient-to-br from-[#4E7D2C] to-[#8CC63F] py-24 md:py-32" data-testid="vision-section">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <span className="inline-flex items-center gap-2 font-poppins text-xs font-bold uppercase tracking-[0.22em] text-white/90">
            <span className="h-px w-8 bg-[#FFD700]" />
            {t("apropos", "vision_kicker")}
          </span>
          <Reveal>
            <blockquote className="mt-6 max-w-4xl">
              <p className="text-justify font-montserrat text-2xl font-medium italic leading-snug text-black sm:text-3xl lg:text-4xl" data-testid="vision-text">
                {lang === "fr" ? "« " : "\u201C"}{t("apropos", "vision_text")}{lang === "fr" ? " »" : "\u201D"}
              </p>
              <footer className="mt-6 font-poppins text-sm font-semibold not-italic text-white md:text-base" data-testid="vision-attribution">
                — {t("apropos", "vision_attribution")}
              </footer>
            </blockquote>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
